import axios from '@fs/zion-axios'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

import { ARTIFACT_CATEGORY } from './artifactsConstants'

// 255 is the max length for filenames on common filesystems
const MAX_FILENAME_LENGTH = 255

/**
 * Checks if the given artifact is an image (only type currently allowed) and has associated audio
 * @param {Object} artifact
 * @returns {boolean}
 */
export function hasAssociatedAudio(artifact) {
  const { category, associatedArtifacts } = artifact
  return Boolean(
    category === ARTIFACT_CATEGORY.IMAGE &&
    associatedArtifacts &&
    associatedArtifacts.artifact.length &&
    associatedArtifacts.artifact.filter((a) => a.category === ARTIFACT_CATEGORY.AUDIO).length > 0
  )
}

/**
 * Fetch the blob for a given artifact, or photo + audio blobs for phaudios
 * @param {Object} artifact artifact to download
 * @returns {Blob[]} array of blobs (usually contains just one blob; will have 2 for photos with associated audio)
 */
export async function fetchArtifactBlobs(artifact) {
  // Download audio with higher-quality originalUrl; other artifacts with url
  const url = artifact.category === ARTIFACT_CATEGORY.AUDIO ? artifact.originalUrl : artifact.url
  let artifactBlob = null
  try {
    const { status, data } = await axios.get(url, {
      responseType: 'blob',
    })
    if (status === 200) {
      artifactBlob = data
    } else {
      throw status
    }
  } catch (error) {
    console.error(`Error fetching artifact ${artifact.id}`)
  }

  if (hasAssociatedAudio(artifact)) {
    const [associatedAudioBlob] = await fetchArtifactBlobs(artifact.associatedArtifacts.artifact[0])
    return [artifactBlob, associatedAudioBlob]
  }

  return [artifactBlob]
}

/**
 * Generate a unique filename <= 255 chars based on artifact ID and original filename
 * @param {Object} artifact
 * @returns {string} string with artifact ID, original filename, and extension
 */
export function calculateFilename(artifact) {
  let filename = `${artifact.id}_${artifact.originalFilename}`

  // If the name is too long, truncate the name and reappend the extension
  if (filename.length > MAX_FILENAME_LENGTH) {
    const extensionPosition = filename.lastIndexOf('.')
    const extension = extensionPosition > 0 ? filename.substring(extensionPosition) : ''
    let name = extensionPosition > 0 ? filename.substring(0, extensionPosition) : filename
    name = name.substring(0, MAX_FILENAME_LENGTH - extension.length)
    filename = name + extension
  }
  return filename
}

export async function addArtifactsToZip(artifacts, zip, onArtifactComplete = () => {}) {
  const artifactPromises = artifacts.map(async (artifact) => {
    const artifactBlobs = await fetchArtifactBlobs(artifact)
    // Contains one blob, or two for photo + audio
    artifactBlobs.forEach((blob, i) => {
      // Name associated audio with the same ID as its parent photo, to keep together
      const filename = i === 0 ? calculateFilename(artifact) : `${artifact.id}_audio.mp3`
      zip.file(filename, blob)
    })
    onArtifactComplete()
  })

  await Promise.all(artifactPromises)
}

/**
 * Downloads the given batch of artifacts, zips it up, and downloads it
 * @param {Object[]} artifacts
 * @param {string} zipName name (excluding extension) to download zip file as
 * @param {function} onArtifactComplete called when an individual artifact has finished downloading
 * @param {function} onBatchComplete called when all artifacts have downloaded and the zip is complete
 */
export async function downloadArtifactBatch(artifacts, zipName, onArtifactComplete, onBatchComplete) {
  const zip = new JSZip()

  await addArtifactsToZip(artifacts, zip, onArtifactComplete)

  const zipBlob = await zip.generateAsync({ type: 'blob' })
  saveAs(zipBlob, `${zipName}.zip`)

  onBatchComplete()
}
